import React, {useState, useEffect} from 'react';
import bbox from '@turf/bbox';
import us from 'us';
import PropTypes from 'prop-types';

import ReactMapGL, {
  Source,
  Layer,
  Popup,
  Marker,
  FlyToInterpolator,
  WebMercatorViewport,
  NavigationControl
} from 'react-map-gl';

import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';

// Loading error fix
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

import './Map.scss';

// import RasterToggle from './RasterToggle';

import {
  formatNumber,
  getAttributeText,
  stateFIPS,
  cityoverrides
} from '../../Utilities/Helpers';
import calcZoom from '../../Utilities/calcZoom';
import {
  getStatesLayer,
  getCountiesLayer,
  getCountyLines,
  highlightStateLayer,
  highlightCountyLayer,
  zoomChange
} from './layerStyles';


// const getImperviousInfo = async (lngLat) => {
//   // Convert coordinates to EPSG:3857 projection bbox
//   const r = 6378137 * Math.PI * 2;
//   const x = (lngLat[0] / 360) * r;
//   const sin = Math.sin(lngLat[1] * Math.PI / 180);
//   const y = 0.25 * Math.log((1 + sin) / (1 - sin)) / Math.PI * r;
//   const bbox = (x-50) + ',' + (y-50) + ',' + (x+50) + ',' + (y+50);

//   const url = `https://www.mrlc.gov/geoserver/mrlc_display/NLCD_2019_Impervious_L48/wms?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetFeatureInfo&BBOX=${bbox}&CRS=EPSG:3857&WIDTH=256&HEIGHT=256&LAYERS=mrlc_display%3ANLCD_2019_Impervious_L48&STYLES=&FORMAT=image/png&QUERY_LAYERS=mrlc_display%3ANLCD_2019_Impervious_L48&INFO_FORMAT=application/json&I=128&J=128`;

//   return fetch(url)
//     .then(res => res.json())
//     .catch(false);
// };



export default function Map({
  climateOption,
  countyClimateTrends,
  countyColors,
  countyHazardMapData,
  hazardOptions,
  isCountyLevel,
  mode,
  range,
  searchLocation,
  stateClimateTrends,
  stateColors,
  stateHazardMapData,
  handleOptionsChange
}) {
  const [clickFilter, setClickFilter] = useState(['in', 'AFFGEOID', '']);
  const [tooltip, setTooltip] = useState(null);
  const [popup, setPopup] = useState(null);
  const [viewport, setViewport] = useState({
    latitude: 43.4,
    longitude: -88.12,
    zoom: 4.8,
    minZoom: 3
  });
  // const [showImpervious, setShowImpervious] = useState(false);
  // const [showTreeCanopy, setShowTreeCanopy] = useState(false);
  const [zoomingToClick, setZoomingToClick] = useState(false);
  useEffect(() => {
    const map = document.getElementById('map');
    const zoom = calcZoom(map.offsetHeight, map.offsetWidth, viewport.zoom);
    setViewport({...viewport, zoom: zoom});
  }, []);
  

  useEffect(() => {
    if (searchLocation.lat && searchLocation.lng) {
      if (searchLocation.zoom) {
        setViewport({
          ...viewport,
          latitude: searchLocation.lat,
          longitude: searchLocation.lng,
          zoom: searchLocation.zoom,
          transitionInterpolator: new FlyToInterpolator(),
          transitionDuration: 1500
        });
      } else {
        setViewport({
          ...viewport,
          latitude: searchLocation.lat,
          longitude: searchLocation.lng,
          transitionInterpolator: new FlyToInterpolator(),
          transitionDuration: 1500
        });
      }
    }
  }, [searchLocation]);


  const handleMapClick = async (event) => {
    if(event.type === 'click') {
      let feature = event.features[0];
      if (feature && (feature.layer.id==='states' || feature.layer.id==='counties')) {
        // if (feature.layer.id==='counties') {
        //   const rasterInfo = await getImperviousInfo(event.lngLat);
        //   console.log(rasterInfo);
        // }
        
        // Calculate offsets to adjust the map zoom on click
        const leftPad = document.getElementById('template').offsetWidth + 24;
        const bottomPad = document.getElementById('opts').offsetHeight + 24;

        const [minLng, minLat, maxLng, maxLat] = bbox(feature);
        const {longitude, latitude, zoom} = new WebMercatorViewport(viewport).fitBounds([[minLng, minLat], [maxLng, maxLat]], {padding: {
          bottom: bottomPad,
          top: 24,
          left: leftPad,
          right: 34
        }});

        setViewport({
          ...viewport,
          longitude,
          latitude,
          zoom,
          transitionInterpolator: new FlyToInterpolator(),
          transitionDuration: 1500
        });
        setClickFilter(['in', 'AFFGEOID', feature.properties.AFFGEOID]);
        handleOptionsChange([[1, 'tab'], [{
          id: feature.layer.id === 'states' ? feature.properties.STUSPS : feature.properties.GEOID,
          name: feature.layer.id === 'states' ? us.lookup(feature.properties.NAME).name: feature.properties.NAME,
          state: feature.layer.id === 'counties' ? us.lookup(feature.properties.STATEFP).abbr : undefined
        }, 'location'], [true, 'isCountyLevel']], 'multiple');

        setZoomingToClick(true);
        setTimeout(() => {
          setZoomingToClick(false);
        }, 1600);
      }
    }
  };


  const handleError = (event) => {
    if (event && event.error !== 'Error: Not Found') {
      console.error(event);
    }
  };


  const getNumber = (mode, data, dataKey, featureId) => {
    let number;

    try {
      number = data[dataKey][featureId];

      if (number === undefined) {
        throw 'undefined';
      }
    } catch {
      number = 0;
    }

    return mode ? number.toFixed(2) : formatNumber(number);
  };


  const chooseData = (isClimate, isState) => {
    if (isState) {
      return isClimate ? stateClimateTrends : stateHazardMapData;
    } else {
      return isClimate ? countyClimateTrends : countyHazardMapData;
    }
  };


  const handleHover = (event) => {
    const feature = event.features && event.features[0];
    let tooltipInfo, text;
    
    if(feature) {
      const opt = mode ? climateOption : hazardOptions[1];
      const {attrName, unit, tooltipUnit} = getAttributeText(mode, opt);
      const formattedUnits = mode ? tooltipUnit + ' per decade' : unit;
      const isState = feature.layer.id === 'states';
      const data = chooseData(mode, isState);
      const number = <b>{getNumber(mode, data, opt, isState ? feature.properties.STUSPS : feature.properties.GEOID)}</b>;
      const rangeText = <span> between <b>{range.start}</b> and <b>{range.end}</b>.</span>;
      const locationText = <b>{feature.properties.NAME}{isState ? '' : cityoverrides.includes(feature.properties.GEOID) ? ' City' : ' County'}</b>;

      if (mode) {
        text = <p> In {locationText} the {attrName} has changed by {number} {formattedUnits} {rangeText}</p>;
      } else {
        let t = attrName === 'Total Reported Episodes' ? ' reported events' : ` ${formattedUnits} of reported ${attrName.split(' ').slice(2).join(' ').toLowerCase()}`;
        text = <p>{locationText}<b>, {stateFIPS[feature.properties.STATEFP]}</b> has experienced <b>{formattedUnits === '' ? '$' : ''}</b> {number} {t} {rangeText}</p>;
      }

      tooltipInfo = {
        latitude: event.lngLat[1],
        longitude: event.lngLat[0],
        text
      };
    } else {
      tooltipInfo = null;
    }

    setTooltip(tooltipInfo);
  };


  const handleViewportChange = (nextVP) => {
    // Clamp latitude to range
    if (nextVP.latitude > 49.82 || nextVP.latitude < 31.65) {
      nextVP.latitude = viewport.latitude;
    }

    // Clamp longitude to range
    if (nextVP.longitude > -71.13 || nextVP.longitude < -98.85) {
      nextVP.longitude = viewport.longitude;
    }

    // Enforce minimum zoom 
    if (nextVP.zoom < 3.0) {
      nextVP.zoom = 3.0;
    }

    // Update isCountyLevel when zoom passes threshold
    if (nextVP.zoom !== viewport.zoom) {
      if (!zoomingToClick && !isCountyLevel && nextVP.zoom >= zoomChange) {
        handleOptionsChange(true, 'isCountyLevel');
      }
      
      if (!zoomingToClick && isCountyLevel && nextVP.zoom <= zoomChange) {
        handleOptionsChange(false, 'isCountyLevel');
      }
    }
    
    setViewport(nextVP);
  };


  const handleMarkerMouseEnter = (name) => {
    setPopup(name);
  };


  const handleMarkerMouseLeave = () => {
    setPopup(null);
  };


  return (
    <div id="map">
      <ReactMapGL
        {...viewport}
        height= "100%"
        width= "100%"
        interactiveLayerIds={['states', 'counties']}
        onViewportChange={nextViewport => handleViewportChange(nextViewport)}
        mapboxApiAccessToken="pk.eyJ1IjoiYmVuZWNrIiwiYSI6ImNrczd4ZGhjOTB0N3MzMGxobHQ4aXp0bm0ifQ.yRPTkmXuTvIvOmY-IrsYQg"
        mapStyle="mapbox://styles/beneck/ckn51ng3n03z917mlu89nmi91"
        onClick={handleMapClick}
        onError={handleError}
        onHover={handleHover}
        dragRotate={false}
      >
        <Source type="vector" url="mapbox://beneck.8gy5j8h3">
          <Layer beforeId="waterway-label" {...getStatesLayer(stateColors, isCountyLevel)}/>
        </Source>
        <Source type="vector" url="mapbox://beneck.3at6c9tb">
          <Layer beforeId="waterway-label" {...getCountiesLayer(countyColors, isCountyLevel)}/>
        </Source>
        <Source type="vector" url="mapbox://beneck.3at6c9tb">
          <Layer beforeId="waterway-label" {...getCountyLines(isCountyLevel)}/>
        </Source>
        <Source type="vector" url="mapbox://beneck.8gy5j8h3">
          <Layer beforeId="waterway-label" {...highlightStateLayer} filter={clickFilter}/>
        </Source>
        <Source type="vector" url="mapbox://beneck.3at6c9tb">
          <Layer beforeId="waterway-label" {...highlightCountyLayer} filter={clickFilter}/>
        </Source>

        {/* <Source
          type="raster"
          tiles={['https://www.mrlc.gov/geoserver/wms?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image%2Fpng&TRANSPARENT=true&LAYERS=mrlc_display%3ANLCD_2019_Impervious_L48&TILED=true&SRS=EPSG%3A3857&jsonLayerId=allconusNlcd2019ImperviousSurface&STYLES=&WIDTH=256&HEIGHT=256&CRS=EPSG%3A3857&BBOX={bbox-epsg-3857}']}
        >
          <Layer
            beforeId="waterway-label"
            id='imperv-raster'
            type='raster'
            paint={{}}
            layout={{ visibility: (isCountyLevel && showImpervious) ? 'visible' : 'none' }}
          />
        </Source>

        <Source
          type="raster"
          tiles={['https://www.mrlc.gov/geoserver/wms?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image%2Fpng&TRANSPARENT=true&LAYERS=mrlc_display%3ANLCD_2016_Tree_Canopy_L48&TILED=true&SRS=EPSG%3A3857&jsonLayerId=allconusNlcd2016TreeCanopy&STYLES=&WIDTH=256&HEIGHT=256&CRS=EPSG%3A3857&BBOX={bbox-epsg-3857}']}
        >
          <Layer
            beforeId="waterway-label"
            id='tree-raster'
            type='raster'
            paint={{}}
            layout={{ visibility: (isCountyLevel && showTreeCanopy) ? 'visible' : 'none' }}
          />
        </Source> */}

        <NavigationControl className="map-nav" showCompass={false} />

        <Markers
          onMarkerMouseEnter={handleMarkerMouseEnter}
          onMarkerMouseLeave={handleMarkerMouseLeave}
          searchLocation={searchLocation}
        />

        {popup && <Popup
          tipSize={5}
          anchor="top"
          longitude={searchLocation.lng}
          latitude={searchLocation.lat}
          closeOnClick={false}
          closeButton={false}
          onClose={() => setPopup(null)}
        >
          <div className="popup-text">
            <div className="popup-title">{popup}</div>
          </div>
        </Popup>}

        {!popup && tooltip && <Popup
          tipSize={5}
          anchor="top"
          longitude={tooltip.longitude}
          latitude={tooltip.latitude}
          closeOnClick={false}
          closeButton={false}
          onClose={() => setTooltip(null)}
        >
          <div className="popup-text">
            <div className="popup-num">
              {tooltip.text}
            </div>
          </div>
        </Popup>}
      </ReactMapGL>

      {/* <RasterToggle
        setShowImpervious={setShowImpervious}
        setShowTreeCanopy={setShowTreeCanopy}
        isCountyLevel={isCountyLevel}
      /> */}
    </div>
  );
}

Map.propTypes = {
  climateOption: PropTypes.string,
  hazardOptions: PropTypes.array,
  isCountyLevel: PropTypes.bool,
  mode: PropTypes.bool,
  range: PropTypes.object,
  countyClimateTrends: PropTypes.object,
  countyHazardMapData: PropTypes.object,
  searchLocation: PropTypes.object,
  stateClimateTrends: PropTypes.object,
  stateHazardMapData: PropTypes.object,
  handleOptionsChange: PropTypes.func,
  stateColors: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string
  ]),
  countyColors: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string
  ])
};



const Markers = ({onMarkerMouseEnter, onMarkerMouseLeave, searchLocation}) => {
  if (searchLocation.lat !== null && searchLocation.lng !== null) {
    return (
      <Marker
        latitude={searchLocation.lat}
        longitude={searchLocation.lng}
      >
        <div
          className="marker"
          onMouseEnter={() => onMarkerMouseEnter(searchLocation.name)}
          onMouseLeave={onMarkerMouseLeave}
        >
          <div className="marker-dot"></div>
        </div>
      </Marker>
    );
  } else {
    return '';
  }
};

Markers.propTypes = {
  onMarkerMouseEnter: PropTypes.func,
  onMarkerMouseLeave: PropTypes.func,
  searchLocation: PropTypes.object
};